import { Stack } from '@mui/material'
import { i18WithParams as t } from '@shared/locales'
import { AdyenCardPayment, submitPaymentForm, TOnPaymentCompleted } from 'adyen_card_payment'
import { ButtonActions } from 'button_actions'
import { enqueueSnackbar } from 'notistack'
import { Types } from 'service-api'
import { captureError } from 'service-api/src/shared'
import { buildErrorMessage } from '../shared/error'

interface AddCartFormProps {
  orderPayment: Types.OrderPaymentAdminDtoEx
  locationUserProfile: Types.LocationUserProfileAdminDto
  handleCancelPayment: () => void
  handleConfirmPayment: (result: TOnPaymentCompleted) => void
  isAdmin?: boolean
}

let adyenPromiseResolve: (value: boolean | PromiseLike<boolean>) => void, adyenPromiseReject: (err: unknown) => void

export const AddCartPaymentForm = ({
  orderPayment,
  locationUserProfile,
  handleCancelPayment,
  handleConfirmPayment,
  isAdmin = true,
}: AddCartFormProps) => {
  const onCancelPayment = async () => {
    try {
      await handleCancelPayment()
    } catch (error) {
      enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
      captureError(error as Error)
    }
  }

  const onSubmitPayment = async () => {
    const promise = new Promise<boolean>((resolve, reject) => {
      adyenPromiseResolve = resolve
      adyenPromiseReject = reject
    })

    // this is only initiating the payment, the function is synchronous
    // the actual payment is done in the AdyenCardPayment component and
    // the result is passed to the onPaymentCompleted callback or onError
    // on those callbacks the promise will be resolved or rejected
    submitPaymentForm('paymentFormId')
    return promise
  }

  const onPaymentCompleted = async (result: TOnPaymentCompleted) => {
    if (result.failed) {
      enqueueSnackbar(t('payment.error'), { variant: 'error' })
      adyenPromiseResolve(false)
      return
    }
    try {
      await handleConfirmPayment(result)
      adyenPromiseResolve(true)
    } catch {
      adyenPromiseResolve(false)
    }
  }

  const onPaymentError = (err: Error) => {
    if (err.message === 'invalid-form') {
      adyenPromiseResolve(false)
    } else {
      captureError(err)
      if (adyenPromiseReject) {
        adyenPromiseReject(err)
      } else {
        // eslint-disable-next-line no-console
        console.error(err)
      }
    }
  }

  return (
    <>
      <AdyenCardPayment
        sessionId={orderPayment.payment?.paymentIntentId}
        sessionData={orderPayment.clientSecret}
        currency="USD"
        amount={0}
        paymentFormId="paymentFormId"
        storeDetailsMsg="Customer agreed to save card for future payments"
        isAdmin={isAdmin}
        showRegularPaymentMethods={true}
        showStoredPaymentMethods={false}
        onPaymentCompleted={onPaymentCompleted}
        onError={onPaymentError}
        billingAddress={{
          street: `${locationUserProfile?.streetAddress || ''} ${locationUserProfile?.streetAddress2 || ''}`.trim(),
          postalCode: locationUserProfile?.zipCode || '',
          city: locationUserProfile?.city || '',
          country: 'US',
          houseNumberOrName: '',
          stateOrProvince: locationUserProfile?.state || '',
        }}
      />
      <Stack direction="row" sx={{ mt: 3 }}>
        <ButtonActions
          invertButtons
          actionEnabled
          onAction={onSubmitPayment}
          onActionLabel={t('global.btn.addCard')}
          actionVariant="secondary"
          onCancel={onCancelPayment}
        />
      </Stack>
    </>
  )
}
